import { useMutation } from '@tanstack/react-query';
import services from 'services/services';
import { useAuth } from 'context/AuthContext';

/**
 * @typedef {Object} AccountSettingsUpdate
 * @property {boolean} [is_unique_phone_number_enabled]
 * @property {number} [panelist_fatigue_email]
 * @property {number} [panelist_fatigue_sms]
 * @property {number} [panelist_fatigue_scheduling]
 * @property {number} [panelist_fatigue_incentive]
 * @property {number} [rbac_incentives]
 * @property {number} [pv_points]
 * @property {number} [points_to_money]
 * @property {number} [points_redeem_min]
 * @property {string[]} [allowed_countries]
 * @property {boolean} [allow_all_countries]
 */

/** @typedef {Omit<import('@tanstack/react-query').UseMutationOptions<unknown, Error, AccountSettingsUpdate, unknown>, 'mutationKey' | 'mutationFn'>} UseUpdateAccountSettingsMutationOptions */

/**
 * Update account settings via the bulk PUT endpoint.
 *
 * Merges the provided settings into the current account state
 * and submits the full payload required by `PUT /accounts/:id`.
 *
 * @example
 * const { mutate } = useUpdateAccountSettingsMutation({
 *     onSuccess: () => auth.checkAuth()
 * });
 * mutate({ is_unique_phone_number_enabled: false });
 *
 * @param {UseUpdateAccountSettingsMutationOptions} [options={}]
 */
export const useUpdateAccountSettingsMutation = (options = {}) => {
    const auth = useAuth();

    return useMutation({
        ...options,
        mutationKey: ['update-account-settings'],
        mutationFn: settingsUpdate => {
            const account = auth.account;

            return services.updateAccount({
                ...account,
                incentive_currency: account.incentive_currency || 'USD',
                settings: {
                    ...account.settings,
                    ...settingsUpdate
                }
            });
        }
    });
};
